/* @flow */
import { type ContextRouter } from 'react-router-dom';
import qs from 'qs';
import { connect } from 'react-redux';
import { push } from 'connected-react-router';
import * as React from 'react';

import { Box, Button, Flex, Footer, Icon, IconVariant, Space, Text, defaultTheme } from '@sonyged/proc-ui';
import css from '@styled-system/css';
import styled from 'styled-components';

import type { StoreState } from '../../../redux/types';
import { contest, isReloadNeeded } from '../../../redux/selector';
import IntroModal from './IntroModal';
import LoadingIcon from '../../../../assets/images/pages/top/loading@3x.png';
import SushiFieldImg from '../../../../assets/images/pages/sushi/sushi_field@3x.png';
import api from '../../../graphql/api';

type Props = {
  ...ContextRouter,
  goBack: Function,
  start: Function,
  showRank: Function,
  courseContentUid: ?string,
  reloadNeeded: boolean,
};

function SushiFieldPage(props: Props) {
  const { goBack, start, showRank, reloadNeeded, courseContentUid, location } = props;

  React.useEffect(() => {
    if (reloadNeeded) window.location.reload();
  }, [reloadNeeded]);

  const [loading, setLoading] = React.useState(false);

  const [introModal, setIntroModal] = React.useState(() => {
    const params = qs.parse(location.search, { ignoreQueryPrefix: true });
    if (params['skip_intro']) {
      return false;
    }
    return true;
  });

  const next = React.useCallback(() => {
    if (!courseContentUid) throw new Error('Unknown uid');
    setLoading(true);
    api.checkoutActivity({ courseContentUid }).then(() => {
      start();
    });
  }, [start]);

  if (introModal)
    return (
      <PageContainer className="sushi" id="proc-root-page">
        <IntroModal title="はじめに" onNextClick={() => setIntroModal(false)} />
      </PageContainer>
    );

  return (
    <PageContainer className="sushi" id="proc-root-page">
      <MainContainer>
        <Body>
          <TitleContainer>
            <Text
              color="secondary100"
              lineHeight="title"
              fontSize={['mobile.small', 'smallPC.small', 'largePC.medium']}
            >
              フィールド
            </Text>
            <Text lineHeight="title" fontSize={['mobile.huge', 'smallPC.huge', 'largePC.huge']}>
              売れるおすしを考えよう
            </Text>
          </TitleContainer>

          <DescriptionContainer>
            <Text fontSize={['mobile.medium', 'smallPC.medium', 'largePC.medium']}>
              おすしをつくって、もらうお金をきそうよ。
            </Text>
          </DescriptionContainer>

          <RankingButton onClick={showRank}>
            <Icon variant={IconVariant.pizzaRanking} />
            ランキング
          </RankingButton>

          <Illustration src={SushiFieldImg} alt="sushi field illustration" />
        </Body>
        <Footer>
          <NavButton>
            <Button onClick={goBack}>
              <Icon variant={IconVariant.back} />
            </Button>
          </NavButton>
          <Space mx="auto">
            <Footer.MainButton className="next" onClick={next}>
              {loading ? (
                <img alt="loading icon" className="proc-button-icon proc-button-loadingIcon" src={LoadingIcon} />
              ) : (
                'はじめる'
              )}
            </Footer.MainButton>
          </Space>
        </Footer>
      </MainContainer>
    </PageContainer>
  );
}

const PageContainer = styled(Flex)(
  css({
    flexDirection: 'column',
    height: '100%',
    bg: 'tertiary100',
    px: ['16px'],
    py: ['16px', '16px', '36px'],
  })
);

const MainContainer = styled(Flex)(
  css({
    flexDirection: 'column',
    flex: 1,
    width: '100%',
    maxWidth: ['600px', '600px', '800px'],
    borderRadius: ['12px', '12px', '15px'],
    mx: 'auto',
    bg: 'white',
  })
);

const Body = styled(Box)(
  css({
    flex: 1,
    position: 'relative',
    px: ['16px', '16px', '48px'],
  })
);

const TitleContainer = styled(Box)(
  css({
    paddingTop: ['16px', '16px', '38px'],
    paddingBottom: ['16px', '16px', '24px'],
    borderBottomWidth: '1px',
    borderBottomStyle: 'solid',
    borderBottomColor: 'secondary20',
    '> *': { display: 'block' },
  })
);

const DescriptionContainer = styled(Box)(
  css({
    mt: ['24px', '24px', '32px'],
  })
);

const Illustration = styled('img')(
  css({
    position: 'absolute',
    bottom: 0,
    right: 0,
    size: ['160px', '160px', '248px'],

    '@media screen and (max-height: 530px)': {
      size: '100px',
    },
  })
);
Illustration.defaultProps = { theme: defaultTheme };

const RankingButton = styled(Button)(
  css({
    position: 'absolute',
    bottom: ['32px'],
    left: ['26px', '26px', '56px'],
    fontSize: ['mobile.small', 'smallPC.small', 'largePC.xs'],
    height: ['30px', '30px', '36px'],

    '&': {
      paddingLeft: '30px',
      paddingRight: '20px',
    },

    '.proc-icon': {
      position: 'absolute',
      top: '-12px',
      left: 0,
    },

    '*::before': {
      fontSize: ['48px', '48px', '58px'],
    },
  })
);
RankingButton.defaultProps = { theme: defaultTheme };

const NavButton = styled(Box)(
  css({
    position: ['static', 'absolute', 'absolute'],
    top: '12px',
    left: '16px',
    marginRight: 'auto',
    paddingRight: '16px',
  })
);
NavButton.defaultProps = { theme: defaultTheme };

const stateToProps = (state: StoreState) => ({
  courseContentUid: contest.sushi.fieldUid(state),
  reloadNeeded: isReloadNeeded(state),
});

const dispatchToProps = dispatch => ({
  goBack: () => dispatch(push('/sushi')),
  start: () => dispatch(push('/sushi/fields/challenge')),
  showRank: () => dispatch(push('/sushi/fields/rank')),
});

const SushiFieldPageContainer = connect<Props, ContextRouter, _, _, _, _>(
  stateToProps,
  dispatchToProps
)(SushiFieldPage);
export default SushiFieldPageContainer;
